import React, { useEffect, useState } from "react";
import {
	Button,
	Dialog,
	TextField,
	Autocomplete,
	DialogTitle,
	Select,
	MenuItem,
	InputLabel,
	FormControl,
	DialogActions,
	DialogContent,
	DialogContentText,
	Snackbar,
	Stack,
	IconButton,
	CircularProgress,
	Box,
	Typography,
} from "@mui/material";
import { LoadingButton, MobileDateTimePicker } from "@mui/lab";
import ColorSinglePicker from "../../components/ColorSinglePicker";
import axios from "../../utils/axios";
import DeleteIcon from "@mui/icons-material/Delete";
import { useNavigate } from "react-router-dom";

const COLOR_OPTIONS = [
	"#00AB55", // Green
	"#1890FF", // Blue
	"#94D82D",
	"#FFC107",
	"#FF4842", // Red
	"#04297A",
	"#7A0C2E",
];

// Fetch practices based on search query
const fetchPractices = async (query) => {
	try {
		const response = await axios.get("/campaign/getAllPractices", {
			params: { page: 1, limit: 50, search: query },
		});
		return response.data.data;
	} catch (error) {
		console.error("Error fetching practices:", error);
		return [];
	}
};

// Fetch a specific practice by ID
const fetchPracticeById = async (practiceId) => {
	try {
		const response = await axios.get(
			`/campaign/getPracticeDetails?practiceId=${practiceId}`
		);
		return response.data;
	} catch (error) {
		console.error("Error fetching practice by ID:", error);
		return null;
	}
};

// Fetch campaigns based on search query
const fetchCampaigns = async (query) => {
	try {
		const response = await axios.get(`/campaign/getAllLegacyCampaigns`, {
			params: { search: query },
		});
		return response.data.data;
	} catch (error) {
		console.error("Error fetching campaigns:", error);
		return [];
	}
};

// Fetch a specific campaign by ID
const fetchCampaignById = async (legacyId) => {
	try {
		const response = await axios.get(
			`/campaign/getLegacyCampaignTypeDetails?legacyId=${legacyId}`
		);
		return response.data;
	} catch (error) {
		console.error("Error fetching campaign by ID:", error);
		return null;
	}
};

const NewCampaignDialog = ({
	open,
	onClose,
	onSubmit,
	initialValues = {
		name: "",
		type: "",
		practiceId: "",
		legacyId: "",
		scheduledDate: new Date(),
		color: "#1890FF",
	},
	isEdit = false,
}) => {
	const navigate = useNavigate();
	const [loading, setLoading] = useState(false);
	const [snackbarMessage, setSnackbarMessage] = useState("");
	const [formValues, setFormValues] = useState(initialValues || {});
	const [practices, setPractices] = useState([]);
	const [selectedPractice, setSelectedPractice] = useState(null);
	const [practiceSearchQuery, setPracticeSearchQuery] = useState("");
	const [practiceLoading, setPracticeLoading] = useState(false);
	const [campaigns, setCampaigns] = useState([]);
	const [selectedCampaign, setSelectedCampaign] = useState(null);
	const [campaignSearchQuery, setCampaignSearchQuery] = useState("");
	const [campaignLoading, setCampaignLoading] = useState(false);
	const [autoGeneratedName, setAutoGeneratedName] = useState("");

	// Mapping statuses to colors using COLOR_OPTIONS
	const statusColorMap = {
		draft: "#FF4842", // Red
		scheduled: "#1890FF", // Blue
		published: "#00AB55", // Green
	};

	useEffect(() => {
		setFormValues(initialValues);

		if (!isEdit) {
			setSelectedPractice(null);
			setFormValues((prev) => ({ ...prev, practiceId: "" }));
			setSelectedCampaign(null);
			setFormValues((prev) => ({ ...prev, legacyId: "" }));
		}

		const loadInitialPractice = async () => {
			if (isEdit && initialValues.practiceId) {
				setPracticeLoading(true);
				const fetchedPractice = await fetchPracticeById(
					initialValues.practiceId
				);
				setSelectedPractice(fetchedPractice);
				setPracticeLoading(false);
			}
		};

		const loadInitialCampaign = async () => {
			if (isEdit && initialValues.legacyId) {
				setCampaignLoading(true);
				const fetchedCampaign = await fetchCampaignById(initialValues.legacyId);
				setSelectedCampaign(fetchedCampaign);
				setCampaignLoading(false);
			}
		};

		if (isEdit) {
			loadInitialPractice();
			loadInitialCampaign();
		}
	}, [initialValues, isEdit]);

	// Fetch practices whenever the search query changes
	useEffect(() => {
		const loadPractices = async () => {
			setPracticeLoading(true);
			const fetchedPractices = await fetchPractices(practiceSearchQuery);
			setPractices(fetchedPractices);
			setPracticeLoading(false);
		};

		if (practiceSearchQuery.length > 2) {
			loadPractices();
		} else {
			setPractices([]);
		}
	}, [practiceSearchQuery]);

	// Fetch campaigns whenever the search query changes
	useEffect(() => {
		const loadCampaigns = async () => {
			setCampaignLoading(true);
			const fetchedCampaigns = await fetchCampaigns(campaignSearchQuery);
			setCampaigns(fetchedCampaigns);
			setCampaignLoading(false);
		};

        loadCampaigns();
	}, [campaignSearchQuery]);

	// Automatically populate the campaign name
	useEffect(() => {
		if (selectedPractice && selectedCampaign && formValues && formValues.type) {
			const newName = `${selectedPractice.name} - ${selectedCampaign.name} - ${formValues.type}`;
			if (formValues.name === autoGeneratedName || !formValues.name) {
				setFormValues((prevValues) => ({
					...prevValues,
					name: newName,
				}));
				setAutoGeneratedName(newName);
			}
		}
	}, [selectedPractice, selectedCampaign, formValues?.type, autoGeneratedName]);

	const handleInputChange = (e) => {
		const { name, value } = e.target;
		setFormValues({ ...formValues, [name]: value });
		// Reset auto-generated name if user manually changes the name
		if (name === "name" && value !== autoGeneratedName) {
			setAutoGeneratedName("");
		}
	};

	const handleDateChange = (date) => {
		setFormValues({ ...formValues, scheduledDate: date });
	};

	const handleColorChange = (event) => {
		setFormValues({ ...formValues, color: event.target.value });
	};

	const handleDeleteCampaign = async () => {
		const confirmDelete = window.confirm(
			"Are you sure you want to delete this campaign?"
		);
		if (!confirmDelete) return;

		try {
			setLoading(true);
			// Call your delete endpoint, e.g., /campaign/:id
			await axios.delete(`/campaign/${formValues?._id}`);
			setSnackbarMessage("Campaign deleted successfully.");

			// Optionally, close the dialog
			onClose();

			// Optionally, navigate back to the campaigns list or another page
			navigate("/dashboard/campaigns");
		} catch (error) {
			console.error("Error deleting campaign:", error);
			setSnackbarMessage("Failed to delete campaign.");
		} finally {
			setLoading(false);
		}
	};

	// Determine the step based on campaign data
	const determineStep = (campaign) => {
		let stepNumber = 1; // Default to Step 1

		if (campaign.emailHtml) {
			stepNumber = 2; // Navigate to Step 2
		}

		if (campaign.data) {
			stepNumber = 3; // Navigate to Step 3
		}

		return stepNumber;
	};

	// Handle form submission: Wait for the backend to create the campaign,
	// then navigate automatically to the next screen.
	const handleSubmit = async (e) => {
		e.preventDefault();

		// Basic validation to check if required fields are filled
		const { name, type, practiceId, legacyId, scheduledDate, color } =
			formValues;

		if (
			!name ||
			!type ||
			!practiceId ||
			!legacyId ||
			!scheduledDate ||
			!color
		) {
			setSnackbarMessage("Please fill in all required fields.");
			return;
		}

		setLoading(true);
		try {
			const campaign = await onSubmit(formValues, isEdit);
			// Display success message
			setSnackbarMessage(
				isEdit
					? "Campaign updated successfully."
					: "Campaign created successfully."
			);

			// Update the local state with the returned campaign data only if it exists
			if (campaign) {
				setFormValues(campaign);
				setSelectedCampaign(campaign);
				setSelectedPractice(
					campaign.practiceId
						? { practiceId: campaign.practiceId, name: campaign.practiceName }
						: null
				);
			}

			// Close the dialog - no navigation needed
			onClose();
		} catch (error) {
			setSnackbarMessage(
				isEdit
					? "Failed to update the campaign."
					: "Failed to create the campaign."
			);
		} finally {
			setLoading(false);
		}
	};

	// This button is retained for editing so the user can reconfigure the campaign.
	const handleConfigureCampaign = () => {
		if (formValues?._id) {
			const stepNumber = determineStep(formValues);
			navigate(`/dashboard/campaigns/${formValues._id}?step=${stepNumber}`);
		}
	};

	return (
		<>
			<Dialog open={open} onClose={onClose} fullWidth>
				<DialogTitle>
					{isEdit ? "Edit Campaign" : "Create New Campaign"}
				</DialogTitle>
				<form autoComplete="off" noValidate onSubmit={handleSubmit}>
					<DialogContent>
						<DialogContentText>
							{isEdit
								? "Edit the details of your campaign."
								: "Fill in the basic information for your new campaign."}
						</DialogContentText>
						<Stack spacing={3} sx={{ p: 3 }}>
							<FormControl fullWidth>
								<TextField
									label="Campaign Name"
									name="name"
									value={formValues?.name || ""}
									onChange={handleInputChange}
									required
								/>
							</FormControl>

							<FormControl fullWidth required>
								<Autocomplete
									options={practices}
									getOptionLabel={(option) => `${option.name} (${option.status}) ${option.practiceId}`|| ""}
									loading={practiceLoading}
									value={selectedPractice}
									onInputChange={(event, value) =>
										setPracticeSearchQuery(value)
									}
									onChange={(event, newValue) => {
										setSelectedPractice(newValue);
										setFormValues({
											...formValues,
											practiceId: newValue?.practiceId || "",
										});
									}}
									renderOption={(props, option) => {
										const statusColor =
											statusColorMap[option.status] || "#1890FF"; // Default to blue
										return (
											<li {...props}>
												<span>{option.name}</span>
												<span
													style={{
														color: statusColor,
														marginLeft: "8px",
													}}
												>
													({option.status} {option.practiceId})
												</span>
											</li>
										);
									}}
									renderInput={(params) => (
										<TextField
											{...params}
											label="Select Practice"
											variant="outlined"
											required
											InputProps={{
												...params.InputProps,
												endAdornment: (
													<>
														{practiceLoading ? (
															<CircularProgress color="inherit" size={20} />
														) : null}
														{params.InputProps.endAdornment}
													</>
												),
											}}
										/>
									)}
								/>
							</FormControl>

							<FormControl fullWidth required>
								<Autocomplete
									options={campaigns}
									getOptionLabel={(option) => option.name}
									loading={campaignLoading}
									value={selectedCampaign}
									onInputChange={(event, value) =>
										setCampaignSearchQuery(value)
									}
									onChange={(event, newValue) => {
										setSelectedCampaign(newValue);
										setFormValues({
											...formValues,
											legacyId: newValue?.id || "",
										});
									}}
									renderOption={(props, option) => (
										<li
											{...props}
											style={{
												display: "flex",
												flexDirection: "column",
												padding: "10px",
												borderBottom: "1px solid #eee",
											}}
										>
											<span style={{ fontWeight: "bold" }}>{option.name}</span>
											<span style={{ color: "#888" }}>
												Species: {option.species}
											</span>
											<span style={{ color: "#aaa", fontSize: "12px" }}>
												ID: {option.id}
											</span>
										</li>
									)}
									renderInput={(params) => (
										<TextField
											{...params}
											label="Select Campaign"
											variant="outlined"
											required
											InputProps={{
												...params.InputProps,
												endAdornment: (
													<>
														{campaignLoading ? (
															<CircularProgress color="inherit" size={20} />
														) : null}
														{params.InputProps.endAdornment}
													</>
												),
											}}
										/>
									)}
								/>
							</FormControl>

							<FormControl fullWidth required>
								<InputLabel>Campaign Type</InputLabel>
								<Select
									name="type"
									value={formValues?.type || ""}
									onChange={handleInputChange}
								>
									<MenuItem value="email">Email</MenuItem>
									<MenuItem value="text">Text</MenuItem>
								</Select>
							</FormControl>

							<FormControl fullWidth>
								<MobileDateTimePicker
									label="Scheduled Date"
									value={formValues?.scheduledDate || new Date()}
									inputFormat="dd/MM/yyyy hh:mm a"
									onChange={handleDateChange}
									renderInput={(params) => <TextField {...params} required />}
								/>
							</FormControl>

							<FormControl fullWidth>
								<Box>
									<ColorSinglePicker
										value={formValues?.color || "#1890FF"}
										onChange={handleColorChange}
										colors={COLOR_OPTIONS}
									/>
								</Box>
							</FormControl>
							<Box
								display="flex"
								justifyContent="space-between"
								alignItems="center"
								sx={{
									p: 2,
									border: "1px solid #ddd",
									borderRadius: "8px",
									backgroundColor: "#f9f9f9",
								}}
							>
								<Box display="flex" flexDirection="column" sx={{ mr: 4 }}>
									<Typography
										variant="body2"
										sx={{ fontWeight: "bold", color: "#555" }}
									>
										Created By
									</Typography>
									<Typography variant="subtitle2" sx={{ color: "#333" }}>
										{formValues.createdBy?.firstName}{" "}
										{formValues.createdBy?.lastName}
									</Typography>
									<Typography variant="caption" sx={{ color: "#888" }}>
										{new Date(formValues.createdAt).toLocaleString()}
									</Typography>
								</Box>

								<Box display="flex" flexDirection="column">
									<Typography
										variant="body2"
										sx={{ fontWeight: "bold", color: "#555" }}
									>
										Scheduled By
									</Typography>
									<Typography variant="subtitle2" sx={{ color: "#333" }}>
										{formValues.scheduledBy?.firstName}{" "}
										{formValues.scheduledBy?.lastName}
									</Typography>
									<Typography variant="caption" sx={{ color: "#888" }}>
										{new Date(formValues.scheduledAt).toLocaleString()}
									</Typography>
								</Box>
							</Box>
						</Stack>
					</DialogContent>
					<DialogActions>
						{isEdit && formValues?._id && (
							<IconButton color="error" onClick={handleDeleteCampaign}>
								<DeleteIcon />
							</IconButton>
						)}

						<Button
							onClick={onClose}
							variant="contained"
							color="inherit"
							disabled={loading}
						>
							Cancel
						</Button>
						<LoadingButton
							type="submit"
							variant="contained"
							loading={loading}
							loadingIndicator="Loading..."
							disabled={loading}
						>
							{isEdit ? "Save Changes" : "Continue"}
						</LoadingButton>
						{isEdit && formValues?._id && (
							<Button
								type="button" // Prevent form submission
								onClick={handleConfigureCampaign}
								variant="contained"
								color="primary"
								disabled={loading}
							>
								Configure Campaign
							</Button>
						)}
					</DialogActions>
				</form>
			</Dialog>
			<Snackbar
				open={!!snackbarMessage}
				onClose={() => setSnackbarMessage("")}
				message={snackbarMessage}
				autoHideDuration={3000}
			/>
		</>
	);
};

export default NewCampaignDialog;
